<template>
  <div>
    <h2>系统配置</h2>

    <div style="margin-bottom:16px;">
      <el-button type="warning" @click="recalculate" :loading="calculating">重新计算当月统计</el-button>
      <span style="color:#909399;font-size:13px;margin-left:8px;">重新计算会覆盖当月已有统计数据</span>
    </div>

    <el-tabs v-model="activeTab">
      <el-tab-pane label="试剂理论检测次数配置" name="reagent">
        <el-table :data="pagedReagentTypeList" border style="width:100%;" v-loading="reagentLoading">
          <el-table-column type="index" label="序号" width="70" />
          <el-table-column prop="reagentName" label="试剂名称" min-width="120" />
          <el-table-column prop="reagentCode" label="试剂编码" width="120" />
          <el-table-column label="单瓶理论检测次数" width="160" align="center">
            <template v-slot="scope">
              <el-input-number v-if="editingId === scope.row.id" v-model="editForm.standardTestTimes"
                :min="1" size="small" controls-position="right" style="width:120px;" />
              <span v-else>{{ scope.row.standardTestTimes }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="unit" label="单位" width="80" align="center" />
          <el-table-column label="状态" width="80" align="center">
            <template v-slot="scope">
              <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="small">
                {{ scope.row.status === 1 ? '启用' : '停用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="updateTime" label="更新时间" width="170" />
          <el-table-column label="操作" width="100" align="center">
            <template v-slot="scope">
              <template v-if="editingId === scope.row.id">
                <el-button type="text" size="small" @click="saveReagentType(scope.row)">保存</el-button>
                <el-button type="text" size="small" @click="cancelEdit">取消</el-button>
              </template>
              <el-button v-else type="text" size="small" @click="startEdit(scope.row)">编辑</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination">
          <el-pagination background @size-change="handleReagentSizeChange" @current-change="handleReagentCurrentChange"
            :current-page="reagentPage.current" :page-size="reagentPage.pageSize"
            :total="reagentTypeList.length"
            :page-sizes="[20, 50, 100]" layout="total, sizes, prev, pager, next, jumper" />
        </div>
      </el-tab-pane>

      <el-tab-pane label="预警规则配置" name="rule">
        <el-table :data="pagedRuleList" border style="width:100%;" v-loading="ruleLoading">
          <el-table-column prop="ruleName" label="配置项" min-width="150" />
          <el-table-column label="配置值" width="160" align="center">
            <template v-slot="scope">
              <el-input v-if="editingRuleId === scope.row.id" v-model="editRuleForm.ruleValue"
                size="small" style="width:100px;" />
              <span v-else>{{ scope.row.ruleValue }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="ruleUnit" label="单位" width="80" align="center" />
          <el-table-column label="状态" width="80" align="center">
            <template v-slot="scope">
              <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="small">
                {{ scope.row.status === 1 ? '启用' : '停用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="updateTime" label="更新时间" width="170" />
          <el-table-column label="操作" width="100" align="center">
            <template v-slot="scope">
              <template v-if="editingRuleId === scope.row.id">
                <el-button type="text" size="small" @click="saveRule(scope.row)">保存</el-button>
                <el-button type="text" size="small" @click="cancelRuleEdit">取消</el-button>
              </template>
              <el-button v-else type="text" size="small" @click="startRuleEdit(scope.row)">编辑</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination">
          <el-pagination background @size-change="handleRuleSizeChange" @current-change="handleRuleCurrentChange"
            :current-page="rulePage.current" :page-size="rulePage.pageSize"
            :total="ruleList.length"
            :page-sizes="[20, 50, 100]" layout="total, sizes, prev, pager, next, jumper" />
        </div>
      </el-tab-pane>

      <el-tab-pane label="账户设备绑定关系" name="bind">
        <el-row :gutter="20" class="search-bar">
          <el-col :span="6">
            <el-input v-model="bindSearch.accountName" placeholder="账户名称" clearable @keyup.enter="doBindSearch" />
          </el-col>
          <el-col :span="6">
            <el-input v-model="bindSearch.deviceNo" placeholder="设备编号" clearable @keyup.enter="doBindSearch" />
          </el-col>
          <el-col :span="4">
            <el-button type="primary" @click="doBindSearch">查询</el-button>
          </el-col>
        </el-row>
        <div class="bind-tip">此页面仅查看绑定关系，不在此模块维护绑定。</div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import { fetchReagentTypeList, updateReagentType, fetchRuleList, updateRule, calculateStat } from "@/api/reagentMonitor";

export default {
  data() {
    return {
      activeTab: "reagent",
      reagentTypeList: [],
      reagentLoading: false,
      editingId: null,
      editForm: { standardTestTimes: 50 },
      ruleList: [],
      ruleLoading: false,
      editingRuleId: null,
      editRuleForm: { ruleValue: "" },
      bindSearch: { accountName: "", deviceNo: "" },
      reagentPage: { current: 1, pageSize: 20 },
      rulePage: { current: 1, pageSize: 20 },
      calculating: false,
    };
  },
  computed: {
    pagedReagentTypeList() {
      const start = (this.reagentPage.current - 1) * this.reagentPage.pageSize;
      return this.reagentTypeList.slice(start, start + this.reagentPage.pageSize);
    },
    pagedRuleList() {
      const start = (this.rulePage.current - 1) * this.rulePage.pageSize;
      return this.ruleList.slice(start, start + this.rulePage.pageSize);
    },
  },
  created() {
    this.loadReagentTypes();
    this.loadRules();
  },
  methods: {
    async loadReagentTypes() {
      this.reagentLoading = true;
      try {
        const res = await fetchReagentTypeList();
        if (res.code === 200) {
          this.reagentTypeList = res.data || [];
          this.reagentPage.current = 1;
        }
      } catch (e) { console.error(e); }
      finally { this.reagentLoading = false; }
    },
    startEdit(row) {
      this.editingId = row.id;
      this.editForm = { standardTestTimes: row.standardTestTimes };
    },
    cancelEdit() {
      this.editingId = null;
    },
    async saveReagentType(row) {
      try {
        const res = await updateReagentType({
          id: row.id,
          standardTestTimes: this.editForm.standardTestTimes,
        });
        if (res.code === 200) {
          this.$message.success("更新成功");
          this.editingId = null;
          this.loadReagentTypes();
        }
      } catch (e) { console.error(e); }
    },
    async loadRules() {
      this.ruleLoading = true;
      try {
        const res = await fetchRuleList();
        if (res.code === 200) {
          this.ruleList = res.data || [];
          this.rulePage.current = 1;
        }
      } catch (e) { console.error(e); }
      finally { this.ruleLoading = false; }
    },
    startRuleEdit(row) {
      this.editingRuleId = row.id;
      this.editRuleForm = { ruleValue: row.ruleValue };
    },
    cancelRuleEdit() {
      this.editingRuleId = null;
    },
    async saveRule(row) {
      try {
        const res = await updateRule({
          id: row.id,
          ruleValue: this.editRuleForm.ruleValue,
        });
        if (res.code === 200) {
          this.$message.success("更新成功");
          this.editingRuleId = null;
          this.loadRules();
        }
      } catch (e) { console.error(e); }
    },
    doBindSearch() {
      this.$message.info("绑定关系查看功能待实现");
    },
    handleReagentSizeChange(size) {
      this.reagentPage.pageSize = size;
      this.reagentPage.current = 1;
    },
    handleReagentCurrentChange(page) {
      this.reagentPage.current = page;
    },
    handleRuleSizeChange(size) {
      this.rulePage.pageSize = size;
      this.rulePage.current = 1;
    },
    handleRuleCurrentChange(page) {
      this.rulePage.current = page;
    },
    async recalculate() {
      try {
        await this.$confirm("确定重新计算当月统计数据？已有数据将被覆盖。", "确认", { type: "warning" });
        this.calculating = true;
        const now = new Date();
        const statMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const res = await calculateStat(statMonth);
        if (res.code === 200) {
          this.$message.success("统计计算完成");
        }
      } catch (e) {
        if (e !== "cancel") console.error(e);
      } finally {
        this.calculating = false;
      }
    },
  },
};
</script>

<style scoped>
.search-bar { margin-bottom: 16px; }
.pagination { margin-top: 16px; display: flex; justify-content: flex-end; }
.bind-tip { color: #909399; font-size: 13px; padding: 16px 0; }
</style>
